import { Card } from "@/components/ui/card";
import useDocumentTitle from "@/components/ui/common/document-title";
import LogCheckIn from '@/assets/checkin.png'
import CheckInLogs from "./daily-check-in";

import { useState, useEffect, useMemo, useRef } from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { InfoIcon, Loader2 } from "lucide-react"
import { useGetAllHardwareQuery } from "@/services/hardwareApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { requestNotificationPermission } from "@/lib/utils/firebase";
import { toast } from "@/components/ui/use-toast";
import { Messaging, onMessage } from "firebase/messaging";
import { initializeMessaging } from "@/lib/utils/firebase";
import { useGetCheckInInfoQuery } from "@/services/userApi";
import { CheckInResponse } from "@/types/user";

interface FullScreenDocument extends Document {
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
    msExitFullscreen?: () => Promise<void>;
    mozCancelFullScreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
}

interface FullScreenDocumentElement extends HTMLElement {
    msRequestFullscreen?: () => Promise<void>;
    mozRequestFullScreen?: () => Promise<void>;
    webkitRequestFullscreen?: () => Promise<void>;
}

export default function CheckIn() {
    useDocumentTitle("Check In");
    const toastShownRef = useRef(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [showLogs, setShowLogs] = useState(false);

    const [selectedHardware, setSelectedHardware] = useState("")
    const [selectedUser, setSelectedUser] = useState("")
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
    const [notification, setNotification] = useState<Record<string, string>>({});
    const [userData, setUserData] = useState<Record<string, string>>({});
    const {
        data: hardwareData,
        isLoading: ishardwaredataloading,
        error,
        isError,
    } = useGetAllHardwareQuery(
        {
            org_id: orgId,
            query: ""
        }
    );



    const {
        data: checkInInfo,
        refetch,
        isLoading: isCheckInLoading,
        isError: isCheckInError
    } = useGetCheckInInfoQuery({
        org_id: orgId,
        hardware_key: selectedHardware,
        user_id: Number(selectedUser)
    }, {
        skip: !selectedHardware || !selectedUser
    });

    const hardwareOptions = useMemo(() => {
        return Array.isArray(hardwareData?.data) ? hardwareData?.data.map(hardware => {
            return {
                value: hardware.connection_key,
                label: hardware.name
            }
        }) : [];
    }, [hardwareData]);


    // Handle hardware selection
    const handleHardwareChange = (value: string) => {
        setSelectedHardware(value)
        setSelectedUser("")
        setShowLogs(false)
        setUserData({})
        setNotification({})
        toastShownRef.current = false; // Reset toast flag
    }

    // Handle fullscreen
    const toggleFullScreen = () => {
        const doc = document as FullScreenDocument;
        const docEl = document.documentElement as FullScreenDocumentElement;

        if (!isFullScreen) {
            if (docEl.requestFullscreen) {
                docEl.requestFullscreen();
            } else if (docEl.webkitRequestFullscreen) {
                docEl.webkitRequestFullscreen();
            } else if (docEl.msRequestFullscreen) {
                docEl.msRequestFullscreen();
            }
        } else {
            if (doc.exitFullscreen) {
                doc.exitFullscreen();
            } else if (doc.webkitExitFullscreen) {
                doc.webkitExitFullscreen();
            } else if (doc.msExitFullscreen) {
                doc.msExitFullscreen();
            }
        }
        setIsFullScreen(!isFullScreen);
    };

    useEffect(() => {
        const doc = document as FullScreenDocument;
        const handleFullscreenChange = () => {
            const isFullscreenNow = !!(
                doc.fullscreenElement ||
                doc.webkitFullscreenElement ||
                doc.mozFullScreenElement ||
                doc.msFullscreenElement
            );
            setIsFullScreen(isFullscreenNow);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);


    useEffect(() => {
        let unsubscribe: (() => void) | null = null;

        initializeMessaging().then((msgInstance: Messaging | null) => {
            if (!msgInstance) {
                console.error("Firebase Messaging is not initialized.");
                return;
            }

            unsubscribe = onMessage(msgInstance, (payload) => {
                console.log("Message received. ", payload);


                if (payload.data) {
                    setUserData(payload.data as Record<string, string>);

                }

                if (payload.notification) {
                    setNotification(payload.notification as Record<string, string>);
                }
            });
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    useEffect(() => {
        requestNotificationPermission();
    }, []);

    // Modify this useEffect to prevent the loop
    useEffect(() => {
        if (Object.keys(notification).length > 0 && Object.keys(userData).length > 0) {
            const { user_id, hardware_key, status_code } = userData as Record<string, string>;

            if (hardware_key === selectedHardware) {
                if (!toastShownRef.current) {
                    
                    toast({
                        variant: Number(status_code) >= 400 ? "destructive" : Number(status_code) >= 200 ? "success" : "default",
                        title: notification.title || "Check-in",
                        description: notification.body || "New check-in detected",
                    });
                    toastShownRef.current = true;

                    // Only set the user ID if it's not already set to prevent loops
                    if (selectedUser !== user_id) {
                        setSelectedUser(user_id);
                        setShowLogs(false);
                    }
                }
            }
        }
    }, [notification, userData, selectedHardware, selectedUser]);

    // Modify this useEffect to prevent unnecessary refetches but ensure fresh data
    useEffect(() => {
        if (selectedUser && selectedHardware) {
            // Force refetch every time regardless of cache
            refetch();
        }
    }, [selectedUser, selectedHardware, refetch]);


    // Effect to handle showing logs and reset timer
    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (selectedUser && checkInInfo) {
            setShowLogs(true);

            // Reset states after 15 seconds
            timer = setTimeout(() => {
                setSelectedUser("");
                setShowLogs(false);
                setUserData({});
                setNotification({});
                toastShownRef.current = false; // Reset toast flag
            }, 10000);
        }

        // Reset show logs if there's an error
        if (isCheckInError) {
            setShowLogs(false);
            toastShownRef.current = false; // Reset toast flag
            toast({
                variant: "destructive",
                title: "Error",
                description: "Network connection lost. Please try again."
            });
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [selectedUser, checkInInfo, isCheckInError]);

    return (
        <div className={`${isFullScreen ? 'fixed inset-0 bg-white z-50 overflow-auto' : ''}`}>

            {selectedHardware === "" ? <div className="m-6">
                <label className="block text-sm font-medium mb-2">Hardware</label>
                <Select onValueChange={(value) => handleHardwareChange(String(value))} value={selectedHardware}>
                    <SelectTrigger className="w-full max-w-xs capitalize">
                        <SelectValue placeholder="Select hardware" />
                    </SelectTrigger>
                    <SelectContent className="capitalize">
                        {hardwareOptions.map((option, i) => (
                            <SelectItem key={i} value={String(option.value)}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div> : (
                <div className="p-3 flex items-center justify-between gap-4">

                    <button
                        type="button"
                        onClick={() => {
                            setSelectedHardware("")
                            setSelectedUser("")
                            setShowLogs(false)
                        }}
                        className="space-x-2 "
                    >
                        <i className={`fa fa-arrow-left text-primary`}></i>
                        <span>Back</span>
                    </button>

                    {/* Replace the existing fullscreen button with this */}
                    <button
                        type="button"
                        onClick={toggleFullScreen}
                        title={isFullScreen ? "Minimize" : "Full Screen"}
                        className="space-x-2 text-primary"
                    >
                        {isFullScreen ? (
                            <i className="fa fa-compress text-primary text-lg "></i>
                        ) : (
                            <i className="fa fa-expand text-primary text-lg"></i>
                        )}
                    </button>
                </div>
            )}

            {selectedHardware === "" && (
                <div className="container h-[60vh] w-full gap-6 flex justify-center items-center">
                    <InfoIcon className="h-4 w-4 mr-2" />
                    <p className="text-xl font-semibold">Please select hardware to view the logs.</p>
                </div>
            )}

            {selectedHardware !== "" && (
                <div className="w-full">
                    {isCheckInLoading ? (
                        <div className="flex items-center justify-center h-[70dvh]">
                            {/* <Loader2 className="h-8 w-8 animate-spin text-primary" /> */}
                        </div>
                    ) : showLogs ? (
                        <CheckInLogs checkInInfo={checkInInfo as CheckInResponse} name={userData?.user_name} />
                    ) : (
                        <div className="flex items-center justify-center h-[70dvh]">
                            <img src={LogCheckIn} alt="Check In" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
